import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useLocation } from "react-router-dom";
import { GET_MY_DRAFT_GISTS } from "../../services/graphql/queriesMutations";
import { buildWorkspaceGistUrl } from "../../utils/publicUrls";
import styles from "./DraftTreeWorkspacePage.module.css";

type DraftSubject = {
  id: number;
  publicId: string;
  title: string;
  slug?: string | null;
};

type DraftEdit = {
  id: number;
  body: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type DraftVersion = {
  id: number;
  point: string;
  createdAt?: string | null;
  edits?: DraftEdit[] | null;
};

type DraftGist = {
  id: number;
  publicId: string;
  title: string;
  slug?: string | null;
  subjectId: number;
  parentId?: number | null;
  status: "DRAFT" | "PUBLISHED";
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  canEditInPlace: boolean;
  canPublish: boolean;
  lockReasons: string[];
  subject: DraftSubject;
  versions?: DraftVersion[] | null;
};

type DraftTreeNode = DraftGist & {
  children: DraftTreeNode[];
};

type DraftsQueryData = {
  myDraftGists: DraftGist[];
};

type DraftForest = {
  roots: DraftTreeNode[];
  byId: Map<number, DraftTreeNode>;
};

const formatDate = (value?: string | null): string => {
  if (!value) return "unknown date";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown date";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const stripHtml = (value: string): string =>
  value
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const getLatestVersion = (gist: DraftGist): DraftVersion | null => {
  const versions = gist.versions ?? [];
  if (versions.length === 0) return null;

  return versions
    .slice()
    .sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null;
};

const getLatestEdit = (gist: DraftGist): DraftEdit | null => {
  const latestVersion = getLatestVersion(gist);
  const edits = latestVersion?.edits ?? [];
  if (edits.length === 0) return null;

  return edits
    .slice()
    .sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null;
};

const getPreview = (gist: DraftGist): string => {
  const latestVersion = getLatestVersion(gist);
  const latestEdit = getLatestEdit(gist);
  const text = stripHtml(latestEdit?.body || latestVersion?.point || "");

  if (!text) return "No body text yet.";
  if (text.length <= 180) return text;
  return `${text.slice(0, 180).trim()}…`;
};

const buildWorkspaceUrl = (gist: DraftGist, mode: "read" | "advanced" = "advanced"): string =>
  buildWorkspaceGistUrl(gist, mode);


const compareDrafts = (a: DraftGist, b: DraftGist): number => {
  const aTime = new Date(a.updatedAt || a.createdAt || "").getTime() || 0;
  const bTime = new Date(b.updatedAt || b.createdAt || "").getTime() || 0;
  if (aTime !== bTime) return bTime - aTime;
  return a.title.localeCompare(b.title);
};

const buildDraftForest = (drafts: DraftGist[]): DraftForest => {
  const nodes: DraftTreeNode[] = drafts.map((draft) => ({ ...draft, children: [] }));
  const byId = new Map<number, DraftTreeNode>();
  const roots: DraftTreeNode[] = [];

  nodes.forEach((node) => {
    byId.set(node.id, node);
  });

  nodes.forEach((node) => {
    const parent = typeof node.parentId === "number" ? byId.get(node.parentId) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNode = (node: DraftTreeNode) => {
    node.children.sort(compareDrafts);
    node.children.forEach(sortNode);
  };

  roots.sort(compareDrafts);
  roots.forEach(sortNode);

  return { roots, byId };
};

const findDraftRoot = (node: DraftTreeNode, byId: Map<number, DraftTreeNode>): DraftTreeNode => {
  let cursor: DraftTreeNode = node;
  let guard = 0;

  while (typeof cursor.parentId === "number" && byId.has(cursor.parentId) && guard < 60) {
    const parent = byId.get(cursor.parentId);
    if (!parent) break;
    cursor = parent;
    guard += 1;
  }

  return cursor;
};

const countTreeNodes = (node: DraftTreeNode): number => {
  let count = 1;
  node.children.forEach((child) => {
    count += countTreeNodes(child);
  });
  return count;
};

const hasExternalParent = (node: DraftTreeNode, byId: Map<number, DraftTreeNode>): boolean =>
  typeof node.parentId === "number" && !byId.has(node.parentId);

const createOutlineLines = (nodes: DraftTreeNode[], origin: string): string[] => {
  const lines: string[] = [];

  const visit = (node: DraftTreeNode, depth: number) => {
    const indent = "  ".repeat(Math.max(0, depth));
    const url = `${origin}${buildWorkspaceUrl(node, "advanced")}`;
    lines.push(`${indent}- ${node.title} [${node.publicId}]: ${url}`);
    node.children.forEach((child) => visit(child, depth + 1));
  };

  nodes.forEach((root) => visit(root, 0));
  return lines;
};

type DraftTreeNodeViewProps = {
  node: DraftTreeNode;
  focusId: number | null;
  copiedId: number | null;
  onCopyLink: (node: DraftTreeNode) => void;
  depth?: number;
};

const DraftTreeNodeView: React.FC<DraftTreeNodeViewProps> = ({
  node,
  focusId,
  copiedId,
  onCopyLink,
  depth = 0,
}) => {
  const isFocused = focusId === node.id;

  return (
    <li className={`${styles.treeNode} ${isFocused ? styles.focusedNode : ""}`}>
      <article className={styles.nodeCard}>
        <div className={styles.nodeHeader}>
          <div className={styles.nodeTitleGroup}>
            <div className={styles.nodeBadgeRow}>
              <span className={styles.levelBadge}>{depth === 0 ? "Root draft" : `Child draft · L${depth + 1}`}</span>
              <span className={styles.statusBadge}>Draft</span>
              {isFocused && <span className={styles.focusBadge}>Focused</span>}
            </div>
            <h2>{node.title}</h2>
            <p>
              <span>{node.subject?.title || "Unknown subject"}</span>
              <span>Updated {formatDate(node.updatedAt)}</span>
              <span>{node.children.length} child draft{node.children.length === 1 ? "" : "s"}</span>
              <span>{node.publicId}</span>
            </p>
          </div>
          <div className={styles.nodeActions}>
            <Link className={styles.primaryAction} to={buildWorkspaceUrl(node, "advanced")}>
              Continue writing
            </Link>
            <Link className={styles.secondaryAction} to={buildWorkspaceUrl(node, "read")}>
              Open read view
            </Link>
            <button className={styles.secondaryAction} type="button" onClick={() => onCopyLink(node)}>
              {copiedId === node.id ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>

        <p className={styles.previewText}>{getPreview(node)}</p>
      </article>

      {node.children.length > 0 && (
        <ol className={styles.childList}>
          {node.children.map((child) => (
            <DraftTreeNodeView
              key={child.id}
              node={child}
              focusId={focusId}
              copiedId={copiedId}
              onCopyLink={onCopyLink}
              depth={depth + 1}
            />
          ))}
        </ol>
      )}
    </li>
  );
};

const DraftTreeWorkspacePage: React.FC = () => {
  const location = useLocation();
  const [copiedDraftId, setCopiedDraftId] = useState<number | null>(null);
  const [copiedOutline, setCopiedOutline] = useState(false);

  const focusGistPublicId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("gistPublicId") || null;
  }, [location.search]);

  const legacyFocusGistId = useMemo(() => {
    const rawValue = new URLSearchParams(location.search).get("gistId");
    const numericValue = rawValue ? Number(rawValue) : NaN;
    return Number.isFinite(numericValue) ? numericValue : null;
  }, [location.search]);

  const { data, loading, error } = useQuery<DraftsQueryData>(GET_MY_DRAFT_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const drafts = useMemo(() => data?.myDraftGists ?? [], [data?.myDraftGists]);
  const forest = useMemo(() => buildDraftForest(drafts), [drafts]);
  const focusedDraft = focusGistPublicId
    ? Array.from(forest.byId.values()).find((draft) => draft.publicId === focusGistPublicId) ?? null
    : legacyFocusGistId
      ? forest.byId.get(legacyFocusGistId) ?? null
      : null;
  const focusedRoot = focusedDraft ? findDraftRoot(focusedDraft, forest.byId) : null;
  const focusedDraftId = focusedDraft?.id ?? null;
  const visibleRoots = focusedRoot ? [focusedRoot] : forest.roots;
  const visibleDraftCount = visibleRoots.reduce((total, root) => total + countTreeNodes(root), 0);

  const handleCopyLink = async (draft: DraftTreeNode) => {
    const link = `${window.location.origin}${buildWorkspaceUrl(draft, "advanced")}`;

    try {
      await window.navigator.clipboard.writeText(link);
      setCopiedDraftId(draft.id);
      window.setTimeout(() => setCopiedDraftId(null), 2200);
    } catch {
      setCopiedDraftId(null);
      window.prompt("Copy this workspace link:", link);
    }
  };

  const handleCopyOutline = async () => {
    const lines = createOutlineLines(visibleRoots, window.location.origin);
    const output = lines.join("\n");

    try {
      await window.navigator.clipboard.writeText(output);
      setCopiedOutline(true);
      window.setTimeout(() => setCopiedOutline(false), 2200);
    } catch {
      window.prompt("Copy this draft tree outline:", output);
    }
  };

  if (loading && drafts.length === 0) {
    return (
      <main className={styles.page}>
        <section className={styles.shell}>
          <div className={styles.stateCard}>Loading draft tree…</div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.page}>
        <section className={styles.shell}>
          <div className={styles.stateCard}>
            <h1>Draft tree could not be loaded</h1>
            <p>{error.message}</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <section className={styles.shell}>
        <header className={styles.hero}>
          <div className={styles.heroTop}>
            <div>
              <p className={styles.eyebrow}>Author workspace</p>
              <h1>{focusedRoot ? "Draft Tree Workspace" : "All Draft Trees"}</h1>
            </div>
            <div className={styles.heroActions}>
              <Link className={styles.secondaryAction} to="/workspace/drafts">
                My Drafts
              </Link>
              <Link className={styles.secondaryAction} to="/workspace/bulk-authoring">
                Bulk Authoring
              </Link>
            </div>
          </div>
          <p className={styles.heroText}>
            Review private draft Gists as connected trees. This page does not publish anything; it helps you continue writing,
            check child draft structure, and copy workspace links before publication.
          </p>
          <div className={styles.summaryRow}>
            <span className={styles.summaryPill}>{drafts.length} total draft{drafts.length === 1 ? "" : "s"}</span>
            <span className={styles.summaryPill}>{visibleDraftCount} shown here</span>
            <span className={styles.summaryPill}>{forest.roots.length} draft tree{forest.roots.length === 1 ? "" : "s"}</span>
          </div>
        </header>

        {focusedRoot && (
          <section className={styles.focusCard}>
            <div>
              <p className={styles.eyebrow}>Focused tree</p>
              <h2>{focusedRoot.title}</h2>
              <p>
                {focusedDraft && focusedDraft.id !== focusedRoot.id
                  ? `Focused draft: ${focusedDraft.title}`
                  : "This is the root draft for the visible private tree."}
              </p>
              {hasExternalParent(focusedRoot, forest.byId) && typeof focusedRoot.parentId === "number" && (
                <p className={styles.boundaryNote}>
                  This draft tree starts under a published or non-draft parent. The private child drafts are shown here;
                  the parent can be opened separately.
                </p>
              )}
            </div>
            <div className={styles.focusActions}>
              {hasExternalParent(focusedRoot, forest.byId) && typeof focusedRoot.parentId === "number" && (
                <span className={styles.secondaryAction} aria-disabled="true" title="The parent is outside this private draft tree and will get a stable workspace link in the parent/child placement phase.">
                  Parent outside draft tree
                </span>
              )}
              <Link className={styles.primaryAction} to={buildWorkspaceUrl(focusedDraft ?? focusedRoot, "advanced")}>
                Continue focused draft
              </Link>
              <Link className={styles.secondaryAction} to="/workspace/draft-tree">
                Show all trees
              </Link>
            </div>
          </section>
        )}

        <section className={styles.toolbarCard}>
          <div>
            <p className={styles.eyebrow}>Tree tools</p>
            <p>
              Use this as the working map for draft groups created through Linked Draft or Bulk Authoring.
            </p>
          </div>
          <button className={styles.secondaryAction} type="button" disabled={visibleRoots.length === 0} onClick={handleCopyOutline}>
            {copiedOutline ? "Copied outline" : "Copy visible tree outline"}
          </button>
        </section>

        {drafts.length === 0 ? (
          <div className={styles.stateCard}>
            <h2>No drafts yet</h2>
            <p>Create a linked draft or use Bulk Authoring to start a private draft tree.</p>
          </div>
        ) : visibleRoots.length === 0 ? (
          <div className={styles.stateCard}>
            <h2>Focused draft is not available</h2>
            <p>
              The requested draft is not in your private drafts. Open My Drafts and choose an available draft.
            </p>
          </div>
        ) : (
          <ol className={styles.treeList}>
            {visibleRoots.map((root) => (
              <DraftTreeNodeView
                key={root.id}
                node={root}
                focusId={focusedDraftId}
                copiedId={copiedDraftId}
                onCopyLink={handleCopyLink}
              />
            ))}
          </ol>
        )}
      </section>
    </main>
  );
};

export default DraftTreeWorkspacePage;
